// =====================================================================
// apps/admin/src/ui.js — Peças de interface reaproveitadas pelas páginas.
// Sem framework: o app é pequeno o suficiente pra não pagar esse imposto.
// =====================================================================

import { icon } from '../../../packages/ui/icons.js';
import { escapeHtml } from '../../../packages/ui/format.js';

export { icon, escapeHtml };

/** Cabeçalho padrão com título, busca e ação principal. */
export function cabecalho({ titulo, acao, onAcao, busca, onBusca }) {
  const el = document.createElement('div');
  el.className = 'cabecalho';
  el.innerHTML = `
    <h2>${escapeHtml(titulo)}</h2>
    ${busca ? `<input class="busca" placeholder="${escapeHtml(busca)}">` : ''}
    ${acao ? `<button class="primaria">${icon('novo')} ${escapeHtml(acao)}</button>` : ''}`;
  if (acao)  el.querySelector('button').onclick = onAcao;
  if (busca) el.querySelector('input').oninput = (e) => onBusca(e.target.value.toLowerCase());
  return el;
}

/**
 * Tabela simples. colunas: [{th, td(row), cls}]
 *
 * Cada célula carrega o nome da coluna em data-col. No desktop isso não
 * aparece; no celular, o CSS transforma a linha em cartão e usa esse nome
 * como rótulo à esquerda do valor.
 *
 * É isso que evita a tabela que rola para o lado no celular — e rolagem
 * lateral esconde exatamente a coluna que a pessoa foi buscar.
 */
export function tabela(colunas, linhas, vazio = 'Nada por aqui.') {
  if (!linhas.length) return `<p class="vazio">${escapeHtml(vazio)}</p>`;
  return `<table>
    <thead><tr>${colunas.map(c => `<th class="${c.cls || ''}">${escapeHtml(c.th)}</th>`).join('')}</tr></thead>
    <tbody>${linhas.map(l =>
      `<tr>${colunas.map(c =>
        `<td class="${c.cls || ''}" data-col="${escapeHtml(c.th || '')}">${c.td(l)}</td>`
      ).join('')}</tr>`
    ).join('')}</tbody>
  </table>`;
}

/**
 * Modal com formulário. campos: [{nome,label,tipo,valor,req,opcoes,dica,full}]
 * Devolve os dados no onSalvar. Fecha no Esc e no clique fora.
 */
export function modal({ titulo, campos, dados = {}, salvar = 'Salvar', onSalvar, aviso }) {
  const fundo = document.createElement('div');
  fundo.className = 'modal-fundo';

  const campo = (c) => {
    const v = dados[c.nome] ?? c.valor ?? '';
    const req = c.req ? 'required' : '';
    const estilo = c.full ? 'grid-column:1/-1' : '';
    let ctrl;
    if (c.tipo === 'select') {
      ctrl = `<select name="${c.nome}" ${req}>
        ${(c.opcoes || []).map(o => {
          const val = o.valor ?? o;
          const txt = o.texto ?? o;
          return `<option value="${escapeHtml(val)}" ${String(v) === String(val) ? 'selected' : ''}>${escapeHtml(txt)}</option>`;
        }).join('')}
      </select>`;
    } else if (c.tipo === 'textarea') {
      ctrl = `<textarea name="${c.nome}" rows="3" ${req}>${escapeHtml(v)}</textarea>`;
    } else if (c.tipo === 'checkbox') {
      ctrl = `<input type="checkbox" name="${c.nome}" ${v ? 'checked' : ''} style="width:auto">`;
    } else {
      ctrl = `<input type="${c.tipo || 'text'}" name="${c.nome}" value="${escapeHtml(v)}"
                 ${c.step ? `step="${c.step}"` : ''} ${req}>`;
    }
    return `<label style="${estilo}">${escapeHtml(c.label)}${ctrl}
      ${c.dica ? `<small class="dica">${escapeHtml(c.dica)}</small>` : ''}</label>`;
  };

  fundo.innerHTML = `
    <div class="modal">
      <header><h3>${escapeHtml(titulo)}</h3>
        <button class="icone" data-fechar>${icon('erro')}</button></header>
      <form>
        <div class="corpo">
          ${aviso ? `<div class="aviso info">${icon('alerta')} ${aviso}</div>` : ''}
          <div class="linha-form">${campos.map(campo).join('')}</div>
        </div>
        <footer>
          <button type="button" data-fechar>Cancelar</button>
          <button class="primaria" type="submit">${escapeHtml(salvar)}</button>
        </footer>
      </form>
    </div>`;

  const fechar = () => { fundo.remove(); document.removeEventListener('keydown', esc); };
  const esc = (e) => { if (e.key === 'Escape') fechar(); };
  document.addEventListener('keydown', esc);

  fundo.querySelectorAll('[data-fechar]').forEach(b => (b.onclick = fechar));
  fundo.onclick = (e) => { if (e.target === fundo) fechar(); };

  fundo.querySelector('form').onsubmit = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    const out = {};
    for (const c of campos) {
      out[c.nome] = c.tipo === 'checkbox'
        ? fd.get(c.nome) === 'on'
        : (fd.get(c.nome) === '' ? null : fd.get(c.nome));
    }
    const btn = e.target.querySelector('button[type=submit]');
    btn.disabled = true;
    btn.textContent = 'Salvando…';
    try {
      await onSalvar(out);
      fechar();
    } catch (err) {
      btn.disabled = false;
      btn.textContent = salvar;
      const box = fundo.querySelector('.corpo');
      const div = document.createElement('div');
      div.className = 'aviso erro';
      div.textContent = err.message || String(err);
      box.prepend(div);
    }
  };

  document.body.appendChild(fundo);
  const primeiro = fundo.querySelector('input,select,textarea');
  if (primeiro) primeiro.focus();
  return fundo;
}

export function confirmar(msg) {
  return window.confirm(msg);
}

/** Erro de Supabase vira mensagem legível na tela. */
export function checar({ data, error }) {
  if (error) throw new Error(error.message);
  return data;
}

export function badge(txt, cor = 'cinza') {
  return `<span class="badge ${cor}">${escapeHtml(txt)}</span>`;
}
